import React, {useState, FunctionComponent, useEffect} from "react"
import {useForm} from "react-hook-form"
import Button from "@murphy-frontend/web-core/components/Button"
import {yupResolver} from "@hookform/resolvers/yup"
import * as yup from "yup"
import CheckboxInput from "./CheckboxInput"
import {useEditGroupCustomer} from "@murphy-frontend/common/api/Customers/Mutations"
import {
	CustomerBasicInfoDTO,
	CustomersSelectedDTO,
	MurphyBasicCustomerDto,
	UpdateGroupCustomerRequest
} from "@murphy-frontend/common/interfaces/ICustomersApi"
import {useQueryClient} from "@tanstack/react-query"
import queryKeys from "@murphy-frontend/common/api/Users/QueryKeys"
import {MurphyUser} from "@murphy-frontend/common/interfaces/IUsersApi"

export interface CustomerGroupsModel {
	id: string
	modules: string[]
}

interface UserData {
	CustomerId: string
	CustomerUserId: string
	UserId: string
	CustomerGroups: CustomerBasicInfoDTO[]
}

interface EditCustomerGroupsProps {
	translations: Record<any, any>
	licenses: MurphyBasicCustomerDto[]
	userData: UserData
	closeModal: () => void
}

const EditCustomerGroups: FunctionComponent<EditCustomerGroupsProps> = ({
	translations,
	licenses,
	userData,
	closeModal
}) => {
	const [loading, setIsLoading] = useState(false)
	const [disableEdit, setDisableEdit] = useState(false)

	const queryClient = useQueryClient()
	const {mutate: updateGroupCustomerMut, isPending: isLoading} = useEditGroupCustomer()

	const userSchema = yup.object().shape(
		{
			modules: yup.array().nullable().notRequired()
		},
		[]
	)

	const {
		register,
		formState: {errors, dirtyFields, isDirty},
		handleSubmit,
		watch
	} = useForm<CustomerGroupsModel>({
		resolver: yupResolver(userSchema),
		defaultValues: {
			modules: userData.CustomerGroups.length ? userData.CustomerGroups.map(mod => mod.Id.toString()) : []
		}
	})

	const onSubmitHandler = (updateData: CustomerGroupsModel) => {
		const allDirtyFields = Object.entries(dirtyFields)
			.filter(([key, value]) => value === true)
			.map(item => item[0])
		const updateDataToSubmit: any = {}

		Object.entries(updateData).forEach(([key, value]) => {
			const fieldIsDirty = allDirtyFields.includes(key)
			if (fieldIsDirty) {
				updateDataToSubmit[key] = value
			}
		})

		if (updateDataToSubmit) {
			let updateGroupCustomerRequest: UpdateGroupCustomerRequest = {
				CustomerId: userData.CustomerId,
				CustomerUserId: userData.CustomerUserId,
				UserId: userData.UserId,
				CustomerIds: []
			}

			let customerIds: CustomersSelectedDTO[] = []
			licenses.map(el => {
				if (updateData.modules.includes(el.Id)) {
					customerIds.push({
						Id: el.Id,
						Enabled: true
					})
				} else {
					customerIds.push({
						Id: el.Id,
						Enabled: false
					})
				}
			})
			updateGroupCustomerRequest.CustomerIds = customerIds

			if (Object.keys(updateGroupCustomerRequest).length > 0) {
				setIsLoading(true)
				updateGroupCustomerMut(updateGroupCustomerRequest, {
					onSuccess: (data: CustomerBasicInfoDTO[]) => {
						queryClient.setQueryData(queryKeys.list(userData.CustomerId), (oldData: any[]) =>
							oldData.map((user: MurphyUser) =>
								user.UserId === userData.UserId ? {...user, CustomersList: data} : user
							)
						)
						setIsLoading(false)
					},
					onSettled: () => {
						setIsLoading(false)
						closeModal()
					}
				})
			}
		}
	}

	const formEditClass = () => {
		if (disableEdit) return "edit-user-form-container edit-user-form-container--disabled"
		else return "edit-user-form-container"
	}

	return (
		<div className={formEditClass()}>
			<section>
				<h2>{translations.grouplicense}</h2>
			</section>
			<form onSubmit={handleSubmit(onSubmitHandler)}>
				<div className='row field-add-user'>
					<div className='field-row'>
						<div className='field-input-wrapper'>
							<div className='field-input-grid-wrapper'>
								<div className='field-checkbox-grid'>
									{licenses.map(({Id, Name}) => (
										<CheckboxInput
											key={Id}
											value={Id.toString()}
											title={Name}
											register={register}
											name='modules'
											hideIcon={true}
										/>
									))}
								</div>
								{errors.modules && <p className='field-error'>{errors.modules.message}</p>}
							</div>
						</div>
					</div>
				</div>
				<div className='row field-add-user-btns'>
					<Button buttonSize={"btn--medium"} onClick={() => closeModal()}>
						{translations.cancel}
					</Button>
					<Button type='submit' buttonSize={"btn--medium"} isLoading={loading}>
						{translations.updateUser}
					</Button>
				</div>
			</form>
		</div>
	)
}

export default EditCustomerGroups
